// ** Redux
import { createSlice } from "@reduxjs/toolkit";

// ** Actions
import { getUserProfileMain } from "./actions/get-user-profile-main.action";
import { blockUser } from "./actions/block-user.action";
import { unblockUser } from "./actions/unblock-user.action";
import { followUser } from "./actions/follow-user.action";
import { unfollowUser } from "./actions/unfollow-user.action";
import { addFriend } from "./actions/add-friend.action";
import { acceptFriend } from "./actions/accept-friend.action";
import { deleteFriend } from "./actions/delete-friend.action";

// ############################################################

// Initial state
const initialState = {
	loading: false,
	loaded: false,
	loadedFull: false,
	error: false,
	frozen: false,
	errorType: null,
	isAllowed: false,
	login: null,
	id: null,
	avatar: "",
	avatarMedia: {
		file: null,
		height: null,
		width: null,
		mediaType: null,
		contentId: null,
	},
	sex: null,
	languages: [],
	location: {
		city: null,
		region: null,
		country: null,
	},
	preferences: [],
	tastes: [],
	tastesObject: {},
	relationship: null,
	allowLinks: false,
	listOfLinks: [],
	description: "",
	create: null,
	active: null,
	vip: 1,
	online: 0,
	profileStatus: 0,
	privateData: false,
	kids: 0,
	statistics: {
		userImages: 0,
		userVideos: 0,
		userFriends: 0,
		likes: 0,
		followers: 0,
		following: 0,
	},
	search: {
		search_sex: [],
		search_age: {
			from: 0,
			to: 0,
		},
		search_for: [],
	},
	partners: {
		sex: [],
		name: [],
		height: [],
		figure: [],
		zodiak: [],
		hair: [],
		alko: [],
		smoke: [],
		education: [],
		age: [],
	},
	status: {
		blockByMe: false,
		blockMe: false,
		followStatus: 0,
		friendStatus: 0,
		is_report: false,
	},
	tolerances: {
		canComment: false,
		canDeleteFriend: false,
		canFollow: false,
		canInviteFriend: false,
		canLike: false,
		canSendMessage: false,
		canUnfollow: false,
		filterUserSex: false,
		limit: undefined,
	},
	settings: {
		showAge: true,
		showStatusVip: true,
		soundEnable: true,
	},
	fromServer: false,
	locationMd5Hash: "",
};

// ############################################################

const handleUserActions = (state, action) => {
	state.status.blockByMe = action.payload.status.block_by_me;
	state.status.blockMe = action.payload.status.block_me;
	state.status.followStatus = action.payload.status.follow_status;
	state.status.friendStatus = action.payload.status.friend_status;
	state.tolerances.canSendMessage =
		action.payload.tolerances?.can_send_message ?? false;
	state.tolerances.canSendMessageBlockStatus =
		action.payload.tolerances?.send_message_block_status ?? 0;
	state.tolerances.canComment =
		action.payload.tolerances?.can_comment ?? false;
	state.tolerances.canDeleteFriend =
		action.payload.tolerances?.can_delete_friend ?? false;
	state.tolerances.canFollow = action.payload.tolerances?.can_follow ?? false;
	state.tolerances.canInviteFriend =
		action.payload.tolerances?.can_invite_friend ?? false;
	state.tolerances.canLike = action.payload.tolerances?.can_like ?? false;
	state.tolerances.canUnfollow =
		action.payload.tolerances?.can_unfollow ?? false;
	state.tolerances.filterUserSex =
		action.payload.tolerances?.filter_user_sex ?? false;
	state.tolerances.limit = action.payload.tolerances?.limit ?? false;
};

// Slice
const userProfileMainSlice = createSlice({
	name: "user-profile/main",
	initialState,
	reducers: {
		resetUserProfileMain: (state) => {
			Object.assign(state, initialState);
		},
		setUserStatus: (state, action) => {
			state.profileStatus = action.payload;
		},
		setUserLimit: (state, action) => {
			state.tolerances.limit = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getUserProfileMain.pending, (state) => {
			state.loading = true;
			state.error = false;
			state.loaded = false;
			state.frozen = false;
		});
		builder.addCase(getUserProfileMain.fulfilled, (state, action) => {
			state.loading = false;
			state.loaded = true;
			state.loadedFull = true;
			state.error = false;

			const user = action.payload.user || {};

			const isAllowed2 = !(
				user.frozen === true ||
				user.status?.block_by_me ||
				user.status?.block_me
			);

			const coupleData2 = () => ({
				sex: [user.partner_1.sex, user.partner_2.sex],
				name: [
					user.partner_1?.name_is_spam
						? "[SPAM]"
						: user.partner_1.name,
					user.partner_2?.name_is_spam
						? "[SPAM]"
						: user.partner_2.name,
				],

				height: [user.partner_1.height, user.partner_2.height],
				figure: [user.partner_1.figure, user.partner_2.figure],
				zodiak: [user.partner_1.zodiak, user.partner_2.zodiak],
				hair: [user.partner_1.hair, user.partner_2.hair],
				alko: [user.partner_1.alko, user.partner_2.alko],
				smoke: [user.partner_1.smoke, user.partner_2.smoke],
				education: [user.partner_1.education, user.partner_2.education],
				age: [user.partner_1.age, user.partner_2.age],
			});
			const singleData2 = () => ({
				sex: [user.sex],
				name: [
					user.user_data?.name_is_spam
						? "[SPAM]"
						: user.user_data.name,
				],
				height: [user.user_data.height],
				figure: [user.user_data.figure],
				zodiak: [user.user_data.zodiak],
				hair: [user.user_data.hair],
				alko: [user.user_data.alko],
				smoke: [user.user_data.smoke],
				education: [user.user_data.education],
				age: [user.user_data.age],
			});

			if (isAllowed2) {
				state.loading = false;
				state.loaded = true;
				state.loadedFull = true;
				state.error = false;
				state.frozen = !!user.frozen;
				state.login = user.login;
				state.id = user.id;
				state.avatar = user.avatar;
				state.avatarMedia = {
					file: user?.media?.file,
					height: user?.media?.height,
					width: user?.media?.width,
					mediaType: user?.media?.media_type,
					contentId: user?.media?.media_id,
				};
				state.status = {
					blockByMe: user.status?.block_by_me || false,
					blockMe: user.status?.block_me || false,
					followStatus: user.status?.follow_status || 0,
					friendStatus: user.status?.friend_status || 0,
					is_report: user.status?.is_report || false,
				};
				state.settings = {
					showAge: user?.settings?.show_age || true,
					showStatusVip: user?.settings?.show_status_vip || true,
					soundEnable: user?.settings?.sound_enable || true,
				};
				state.vip = user.status_vip;
				state.sex = user.sex;
				state.isAllowed = isAllowed2;
				state.languages = user.languages
					? user.languages.map((lang) => lang)
					: [];
				state.location = {
					city: user?.city_is_spam ? "[SPAM]" : user.city,
					cityId: user.city_id,
					region: user.region,
					regionId: user.region_id,
					country: user.country,
					urlSearch: user.url_search,
				};
				state.preferences = [...(user?.tastes?.sex || [])];
				state.tastes = [
					...(user?.tastes?.sex || []),
					...(user?.tastes?.culture || []),
					...(user?.tastes?.food || []),
					...(user?.tastes?.hobby || []),
					...(user?.tastes?.life_style || []),
					...(user?.tastes?.sport || []),
				];
				state.tastesObject = user?.tastes;
				state.relationship = user.relationship;
				state.allowLinks = user?.allow_links ?? false;
				state.listOfLinks = user?.list_of_links ?? [];
				state.description = {
					isSpam: user?.is_description_spam ?? false,
					plain: user.description_raw,
					parsed: user.description,
					cut: user.description_cut,
				};
				state.create = user.create;
				state.active = user.active;
				state.online = user.online;
				state.profileStatus = user.profile_status;
				state.privateData = user.private_data;
				state.kids = user.kids;
				state.statistics = {
					userGallery:
						(user.statistics?.galleries?.public || 0) +
						(user.statistics?.galleries?.private || 0),
					userFriends: user.statistics?.friends || 0,
					userFollowers: user.statistics?.followers || 0,
					userFollowing: user.statistics?.user_follow || 0,
					likes: user.statistics?.likes || 0,
				};
				state.tolerances = {
					canSendMessage: user.tolerances?.can_send_message || false,
					canSendMessageBlockStatus:
						user.tolerances?.send_message_block_status || 0,
					canComment: user.tolerances?.can_comment || false,
					canDeleteFriend:
						user.tolerances?.can_delete_friend || false,
					canFollow: user.tolerances?.can_follow || false,
					canInviteFriend:
						user.tolerances?.can_invite_friend || false,
					canLike: user.tolerances?.can_like || false,
					canUnfollow: user.tolerances?.can_unfollow || false,
					filterUserSex: user.tolerances?.filter_user_sex || false,
					limit: user.tolerances?.limit || false,
				};
				state.search = {
					search_sex: [
						...Object.keys(user.search_sex).filter(
							(item) => user.search_sex[item],
						),
					],
					search_age: {
						from: user.search_age?.from,
						to: user.search_age?.to,
					},
					search_for: [
						...Object.keys(user.search_for).filter(
							(item) => user.search_for[item],
						),
					],
				};
				state.partners =
					user.sex === "couple" ? coupleData2() : singleData2();
			} else {
				state.loading = false;
				state.loaded = true;
				state.loadedFull = true;
				state.error = false;
				state.isAllowed = isAllowed2;
				state.frozen = user.frozen;
				state.login = user.login;
				state.id = user.id;
				state.avatar = user.avatar;
				state.avatarMedia = {
					file: user?.media?.file,
					height: user?.media?.height,
					width: user?.media?.width,
					mediaType: user?.media?.media_type,
					contentId: user?.media?.media_id,
				};
				state.status = {
					blockByMe: user.status?.block_by_me || false,
					blockMe: user.status?.block_me || false,
					followStatus: user.status?.follow_status || 0,
					friendStatus: user.status?.friend_status || 0,
				};
				state.location = {
					city: user.city,
					cityId: user.city_id,
					region: user.region,
					regionId: user.region_id,
					country: user.country,
				};
				state.vip = user.status_vip;
			}
		});
		builder
			.addCase(getUserProfileMain.rejected, (state) => {
				Object.assign(state, initialState);
				state.loading = false;
				state.error = true;
				state.loaded = false;
			})
			.addCase(blockUser.fulfilled, (state, action) => {
				handleUserActions(state, action);
				state.isAllowed = false;
			})
			.addCase(unblockUser.fulfilled, handleUserActions)
			.addCase(followUser.fulfilled, handleUserActions)
			.addCase(unfollowUser.fulfilled, handleUserActions)
			.addCase(addFriend.fulfilled, handleUserActions)
			.addCase(acceptFriend.fulfilled, handleUserActions)
			.addCase(deleteFriend.fulfilled, handleUserActions);
	},
});

// ############################################################

export const { setUserStatus, setUserLimit } = userProfileMainSlice.actions;

export default userProfileMainSlice.reducer;

// ############################################################
